import { useCallback, useEffect, useState } from 'react';
import { InvoiceStatus } from '@fpc/shared';

/**
 * Reading a document happens after the upload has already answered.
 *
 * `POST /invoices/upload` stores the file, starts extraction in the background
 * and replies at once with the invoice still in its early state, and email
 * intake works the same way. So whoever opens the invoice straight afterwards
 * is looking at a record that is about to change, and has to be told when it
 * has. These are the rules for that, shared by every screen that shows one.
 *
 * Nothing here depends on which extractor is configured. The stub finishes in
 * milliseconds; Claude and Azure Document Intelligence take seconds; the same
 * wait-and-watch behaviour covers all three.
 */

/** Fast enough to feel live, slow enough not to hammer the API. */
export const EXTRACTION_POLL_MS = 2_000;

/**
 * How long to keep asking before giving up.
 *
 * Mirrors the server: `jobs/extractionWorker.ts` treats an invoice that has sat
 * in EXTRACTING for five minutes as abandoned and re-queues it, so waiting
 * longer than that on this screen would be watching something that has already
 * been restarted.
 */
export const EXTRACTION_POLL_CEILING_MS = 5 * 60 * 1000;

/** True while the document is still waiting to be, or being, read. */
export function isExtracting(status: string | undefined): boolean {
  return status === InvoiceStatus.RECEIVED || status === InvoiceStatus.EXTRACTING;
}

/**
 * Flips to `stalled` once `active` has stayed true for the polling ceiling.
 *
 * Polling is bounded on both ends: it only runs while something is being read,
 * and it stops at the ceiling rather than spinning against a server that has
 * gone away. `retry` starts the wait again, for a "check again" button.
 */
export function useExtractionStall(active: boolean): { stalled: boolean; retry: () => void } {
  const [stalled, setStalled] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!active) {
      setStalled(false);
      return;
    }
    const timer = setTimeout(() => setStalled(true), EXTRACTION_POLL_CEILING_MS);
    return () => clearTimeout(timer);
  }, [active, attempt]);

  const retry = useCallback(() => {
    setStalled(false);
    setAttempt((count) => count + 1);
  }, []);

  return { stalled, retry };
}
